import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import debounce from 'lodash/debounce';
import trim from 'lodash/trim';
import { IApiQueryOptions } from 'api/ApiClient';
import { Empty, Input, InputIconWrapper, PageHeader, PageTitle, PaginationFooter, SectionHeader } from 'components';
import ListContext, { ListContextProvider } from 'providers/ListContextProvider';
import { URL } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee, setCurrentEmployeeRequest } from 'store/App';
import { Employers, getEmployerSitesRequest } from 'store/Employers';
import { IStore } from 'store/types';
import { selectCurrentUser } from 'store/User';
import { ApiQueryOperator, IApiList, IApiQueryParam, ISite } from 'types';
import { ContentHeader, SiteDashboardItem, SiteOverviewList } from './components';

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentOrganizationId = currentEmployee?.organizationId;
  const [dashboardSitesResponse, setDashboardSitesResponse] = useState<IApiList<ISite>>();
  const [totalSites, setTotalSites] = useState<number>();
  const dashboardSites = dashboardSitesResponse?.data;

  const { firstName } = useSelector(selectCurrentUser());

  const fetchAdminSiteStats = (queryParams: IApiQueryOptions) => {
    if (currentOrganizationId) {
      dispatch(
        getEmployerSitesRequest(
          currentOrganizationId,
          (response) => {
            setDashboardSitesResponse(response);

            if (!totalSites) {
              setTotalSites(response.total);
            }
          },
          queryParams
        )
      );
    }
  };

  useEffect(() => {
    setTotalSites(null);
  }, [currentOrganizationId]);

  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Employers.GET_EMPLOYER_SITES_REQUEST));
  const setSite = (id: string) => {
    dispatch(setCurrentEmployeeRequest({ ...currentEmployee, siteId: id }));
  };
  const debounceMs = 800;

  return (
    <>
      <ListContextProvider
        fetchData={fetchAdminSiteStats}
        totalSelector={() => dashboardSitesResponse?.total}
        defaultOffset={0}
        defaultLimit={5}
        defaultSort="name"
        persistURL
        dataKey="adminDashboardSites"
        dependencies={currentOrganizationId}
      >
        <ListContext.Consumer>
          {({ methods, components, query }) => {
            const debouncedHandleSearchChange = debounce((newText: string) => {
              if (newText !== '') {
                methods.onFiltersChange([
                  {
                    key: 'name',
                    operator: ApiQueryOperator.like,
                    value: newText,
                  },
                ]);
              } else {
                methods.onFiltersChange([]);
              }
            }, debounceMs);

            const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
              const newText = e.target.value;
              const trimmedNewText = trim(newText);
              debouncedHandleSearchChange(trimmedNewText);
            };
            const { pagination: Pager } = components;
            // set a search text value if refreshing
            const defaultSearchText = (query.q || []).find((q: IApiQueryParam) => q.key === 'name')?.value;

            return (
              <>
                <PageHeader>
                  <PageTitle title={`Welcome back, ${firstName}`} subtitle={`${totalSites || '--'} Sites`} label="Dashboard" />
                </PageHeader>

                <ContentHeader>
                  <SectionHeader isLoading={isLoading} title={`My Sites (${dashboardSitesResponse?.total})`} data-cy="org-dashboard-content-header">
                    <InputIconWrapper icon="Search" iconPosition="left">
                      <Input height="large" placeholder="Search by site name" onChange={onSearchChange} defaultValue={defaultSearchText} />
                    </InputIconWrapper>
                  </SectionHeader>
                </ContentHeader>

                <SiteOverviewList data-cy="org-dashboard-content-sites-list">
                  {isLoading &&
                    [...Array(query?.limit)].map((e, i) => (
                      <SiteDashboardItem key={i} id={i.toString()} isLoading name="" address="" pendingFunds={0} retainer={0} accountBalance={0} sevenDayTotal={0} expiredPayments={0} totalUsers={0} usersRequiringApproval={0} />
                    ))}

                  {dashboardSites?.length === 0 && !isLoading && <Empty container containerHeight={300} icon="No" message="No sites found. Try another search." />}

                  {dashboardSites?.map((datum) => {
                    const address = `${datum.address.address1}, ${datum.address.city}, ${datum.address.state}, ${datum.address.zipCode}`;
                    const goToPendingInvitations = () => {
                      setSite(datum.id);
                      history.push(URL.EmployeesNeedingApproval);
                    };
                    const goToActiveEmployees = () => {
                      setSite(datum.id);
                      history.push(URL.Employees);
                    };
                    const goToPaymentsOverview = () => {
                      setSite(datum.id);
                      history.push(URL.PaymentsOverview);
                    };
                    const goToPaymentsDetail = () => {
                      setSite(datum.id);
                      history.push(URL.PaymentsDetail);
                    };

                    return (
                      <SiteDashboardItem
                        isLoading={isLoading}
                        id={datum.id}
                        key={datum.id}
                        name={datum.name}
                        address={address}
                        customerSiteId={datum.customerSiteId}
                        pendingRechargeBalance={datum.stats.balance.pendingFunds}
                        pendingRechargeDate={datum.stats.balance.updatedAt}
                        pendingFunds={datum.stats.balance.pendingFunds}
                        goToActiveEmployees={goToActiveEmployees}
                        goToPaymentsOverview={goToPaymentsOverview}
                        goToPaymentsDetail={goToPaymentsDetail}
                        goToPendingInvitations={goToPendingInvitations}
                        retainer={datum.settings.retainer}
                        accountBalance={datum.stats.balance.current}
                        sevenDayTotal={datum.stats.payouts.total.net}
                        expiredPayments={datum.stats.payouts.expired}
                        totalUsers={datum.stats.employees.approved}
                        usersRequiringApproval={datum.stats.employees.actionRequired}
                      />
                    );
                  })}

                  <PaginationFooter standalone>{!isLoading && dashboardSites && totalSites > dashboardSites.length && Pager}</PaginationFooter>
                </SiteOverviewList>
              </>
            );
          }}
        </ListContext.Consumer>
      </ListContextProvider>
    </>
  );
};

export default AdminDashboard;
